import React, { useEffect, useRef, useState } from 'react';

interface AudioLevelMeterProps {
  stream?: MediaStream | null;
  analyser?: AnalyserNode | null;
  label?: string;
  className?: string;
  barCount?: number;
  colorClass?: string;
  levelValue?: number;
  peakValue?: number;
}

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

export const AudioLevelMeter: React.FC<AudioLevelMeterProps> = ({
  stream,
  analyser,
  label,
  className = '',
  barCount = 12,
  colorClass = 'bg-emerald-400',
  levelValue,
  peakValue
}) => {
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const dataArrayRef = useRef<Float32Array | null>(null);
  const animationRef = useRef<number | null>(null);
  const usingExternalAnalyserRef = useRef<boolean>(false);
  const [level, setLevel] = useState(0);
  const [peak, setPeak] = useState(0);
  const hasExternalLevels = levelValue !== undefined || peakValue !== undefined;

  useEffect(() => {
    if (!hasExternalLevels) {
      return;
    }
    cleanup();
    if (typeof levelValue === 'number') {
      setLevel(clamp(levelValue, 0, 1));
    }
    if (typeof peakValue === 'number') {
      setPeak(clamp(peakValue, 0, 1));
    }
  }, [hasExternalLevels, levelValue, peakValue]);

  useEffect(() => {
    if (hasExternalLevels) {
      return;
    }
    if (!stream && !analyser) {
      cleanup();
      setLevel(0);
      setPeak(0);
      return;
    }

    if (analyser) {
      bindToExternalAnalyser(analyser);
    } else if (stream) {
      startAnalyser(stream);
    }

    return () => {
      cleanup();
      setLevel(0);
      setPeak(0);
    };
  }, [stream, analyser, hasExternalLevels]);

  const bindToExternalAnalyser = (node: AnalyserNode) => {
    analyserRef.current = node;
    usingExternalAnalyserRef.current = true;
    if (!dataArrayRef.current || dataArrayRef.current.length !== node.fftSize) {
      dataArrayRef.current = new Float32Array(node.fftSize);
    }
    startMeterLoop();
  };

  const startAnalyser = (mediaStream: MediaStream) => {
    if (audioContextRef.current) return;
    // Guard: ensure stream has an audio track
    if (!mediaStream || mediaStream.getAudioTracks().length === 0) {
      console.warn('AudioLevelMeter: provided MediaStream has no audio tracks');
      cleanup();
      return;
    }
    try {
      audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
      analyserRef.current = audioContextRef.current.createAnalyser();
      usingExternalAnalyserRef.current = false;
      analyserRef.current.fftSize = 512;

      const source = audioContextRef.current.createMediaStreamSource(mediaStream);
      source.connect(analyserRef.current);

      const bufferLength = analyserRef.current.fftSize;
      dataArrayRef.current = new Float32Array(bufferLength);

      startMeterLoop();
    } catch (error) {
      console.warn('AudioLevelMeter failed to initialise analyser:', error);
      cleanup();
    }
  };

  const startMeterLoop = () => {
    const update = () => {
      if (!analyserRef.current || !dataArrayRef.current) return;
      const buffer = dataArrayRef.current as Float32Array;
      if (typeof analyserRef.current.getFloatTimeDomainData === 'function') {
        (analyserRef.current as any).getFloatTimeDomainData(buffer);
      } else {
        const temp = new Uint8Array(buffer.length);
        analyserRef.current.getByteTimeDomainData(temp);
        for (let i = 0; i < temp.length; i++) {
          buffer[i] = (temp[i] - 128) / 128;
        }
      }

      let sum = 0;
      for (let i = 0; i < buffer.length; i++) {
        sum += buffer[i] * buffer[i];
      }
      const rms = Math.sqrt(sum / buffer.length);
      const newLevel = clamp(rms * 3, 0, 1);
      setLevel(newLevel);
      setPeak((prev) => (newLevel > prev ? newLevel : prev * 0.98));

      animationRef.current = requestAnimationFrame(update);
    };

    update();
  };

  const cleanup = () => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
    if (!usingExternalAnalyserRef.current && analyserRef.current) {
      analyserRef.current.disconnect();
    }
    analyserRef.current = null;
    if (!usingExternalAnalyserRef.current && audioContextRef.current) {
      audioContextRef.current.close().catch(() => undefined);
      audioContextRef.current = null;
    }
    usingExternalAnalyserRef.current = false;
    dataArrayRef.current = null;
  };

  const activeBars = Math.round(level * barCount);
  const peakBarIndex = Math.min(barCount - 1, Math.round(peak * barCount));

  return (
    <div className={`space-y-2 ${className}`}>
      {label && <div className="text-xs font-medium text-gray-300 uppercase tracking-wide">{label}</div>}
      <div className="flex items-end h-12 space-x-1">
        {Array.from({ length: barCount }).map((_, index) => {
          const isActive = index < activeBars;
          const isPeak = index === peakBarIndex && peak > 0.05;
          const height = ((index + 1) / barCount) * 100;
          const baseClass = 'w-2 rounded-sm transition-colors duration-75';
          const activeClass = isActive ? colorClass : 'bg-gray-700';
          const peakOverlay = isPeak ? ' shadow-[0_0_10px_rgba(34,197,94,0.8)]' : '';
          return (
            <div
              key={index}
              className={`${baseClass} ${activeClass}${peakOverlay}`}
              style={{ height: `${height}%` }}
            />
          );
        })}
      </div>
      <div className="text-[11px] text-gray-400">
        Level: {Math.round(level * 100)}% • Peak: {Math.round(peak * 100)}%
      </div>
    </div>
  );
};

export default AudioLevelMeter;
